
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  UserRoundPen,
  Ellipsis,
  CheckCircle,
  XCircle,
  Ban,
} from "lucide-react";

import useClickOutside from "../../../CustomHooks/useClickOutside";
import UsePermissionsClaim from "../../../CustomHooks/UsePermissionsClaim";
import addLoan from "../../../assets/icons/loanadd.svg";
import loanRepayicon from "../../../assets/icons/loanRepayicon.svg";
import deductIcon from "../../../assets/icons/deductIcon.svg";
import salaryBonus from "../../../assets/icons/salaryBonus.svg";
import salaryDeduct from "../../../assets/icons/salaryDesuct.svg";
import updateAdmins from "../../../assets/icons/updateAdmins.svg";
import updateRoles from "../../../assets/icons/updateRoles.svg";

import DisableUserDialog from "./userList/DisableUserDialog";
import PhoneVerificationDialog from "./userList/PhoneVerificationDialog";

function UserActions({ userData }) {
  const [open, setOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);

  const { dropdownRef } = useClickOutside(() => setOpen(false));
  const { checkPermission } = UsePermissionsClaim();

  const { id, fullName, loanBalance, salary, isPhoneVerified } = userData;

  const permissions = useMemo(
    () => ({
      addLoan: checkPermission("add_loan"),
      addLoanRepayment: checkPermission("add_loan_expense"),
      addDeduction: checkPermission("add_deduction_expense"),
      updateUser: checkPermission("update_user"),
      superAdmin: checkPermission("sup_admin_check"),
      overtime: checkPermission("payroll_addition"),
      salaryDeduct: checkPermission("payroll_deduction"),
      disableUser: checkPermission("disable_user"),
      verification: checkPermission("user_phone_verify"),
    }),
    [checkPermission]
  );

  const actions = [
    {
      show: permissions.addLoan,
      type: "link",
      to: `/expense/add-loan/${id}/${fullName}/${loanBalance}`,
      icon: addLoan,
      label: "Add Loan",
    },
    {
      show: permissions.addLoanRepayment,
      type: "link",
      to: `/expense/add-loan-repayment/${id}/${fullName}/${loanBalance}`,
      icon: loanRepayicon,
      label: "Add Loan Repayment",
    },
    {
      show: permissions.addDeduction,
      type: "link",
      to: `/expense/add-deduction/${id}/${fullName}`,
      icon: deductIcon,
      label: "Add Deduction",
    },
    {
      show: permissions.overtime,
      type: "link",
      to: `/user/add-overtime/${id}/${fullName}/${salary}`,
      icon: salaryBonus,
      label: "Add Overtime Amount",
    },
    {
      show: permissions.salaryDeduct,
      type: "link",
      to: `/user/salary-deduction/${id}/${fullName}/${salary}`,
      icon: salaryDeduct,
      label: "Salary Deduction",
    },
    {
      show: permissions.updateUser,
      type: "link",
      to: `/user/update/${id}`,
      icon: <UserRoundPen size={18} />,
      label: "Update User",
    },
    {
      show: permissions.superAdmin,
      type: "link",
      to: `/user/update-admins/${id}`,
      icon: updateAdmins,
      label: "Update Admins",
    },
    {
      show: permissions.superAdmin,
      type: "link",
      to: `/user/update-roles/${id}/${fullName}`,
      icon: updateRoles,
      label: "Update Roles",
    },
    {
      show: permissions.disableUser,
      type: "button",
      onClick: () => setViewDialog("disable"),
      icon: <Ban size={18} color="red" />,
      label: "Disable User",
    },
    {
      show: permissions.verification,
      type: "button",
      onClick: () => setViewDialog("verify"),
      icon: isPhoneVerified ? (
        <XCircle size={18} color="#d97706" />
      ) : (
        <CheckCircle size={18} color="#0d9488" />
      ),
      label: `${isPhoneVerified ? "Unverify" : "Verify"} Phone`,
    },
  ];

  return (
    <div
      className="table-action-btn"
      ref={dropdownRef}
      onClick={() => setOpen((p) => !p)}
    >
      <Ellipsis />
      {open && (
        <div className="actionsWrap--u grid [&_img]:w-5 [&_a]:text-sm [&_:is(a,button)]:py-1 [&_:is(a,button)]:px-2 [&_:is(a,button)]:border-b-2 [&_:is(a,button)]:border-lg5 [&_:is(a,button)]:flex [&_:is(a,button)]:items-center [&_:is(a,button)]:text-dark-blue [&_span]:pl-2 [&_:is(a,button):hover]:bg-lb6">
          {actions
            .filter((a) => a.show)
            .map((action, idx) =>
              action.type === "link" ? (
                <Link key={idx} to={action.to}>
                  {typeof action.icon === "string" ? (
                    <img src={action.icon} alt={action.label} />
                  ) : (
                    action.icon
                  )}
                  <span>{action.label}</span>
                </Link>
              ) : (
                <button key={idx} onClick={action.onClick}>
                  {action.icon}
                  <span>{action.label}</span>
                </button>
              )
            )}
        </div>
      )}
      {/* Show confirmation dialog for user disable action */}
      {viewDialog === "disable" && (
        <DisableUserDialog
          userID={id}
          userName={fullName}
          closeDialog={() => setViewDialog(null)}
        />
      )}
      {/* Show confirmation dialog for phone Verify / Unverify actions */}
      {viewDialog === "verify" && (
        <PhoneVerificationDialog
          userID={id}
          userName={fullName}
          actionType={isPhoneVerified}
          closeDialog={() => setViewDialog(null)}
        />
      )}
    </div>
  );
}

export default UserActions;
